import React, { useEffect, useState } from 'react';

import { timingBridgeAPI, toolsAPI } from '../../../lib/api';
import { FlagPill, RunningOrder } from './shared';
import ChampionshipEditor, { cleanChampionships } from './ChampionshipEditor';

const TOOL_KEY = 'timing-bridge';
const FIELD = 'w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';
const LABEL = 'block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide';
const BUTTON = 'px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#3a3a3a] disabled:opacity-40 text-white text-sm rounded transition-colors';

const ADD_SERIES = '__add__';

const PROVIDERS = [
  { key: 'auto', label: 'Detect from the URL' },
  { key: 'speedhive', label: 'Speedhive (MYLAPS)' },
  { key: 'alpha', label: 'Alpha Timing' },
  { key: 'apex', label: 'Apex Timing' },
];

const STREAMS = [
  { key: 'legacy', label: 'legacy', hint: 'The data stream the existing graphics use: the one the old scraper sent to ("Motor Racing - BSM" etc.).' },
  { key: 'tower', label: 'tower', hint: 'Running order, formatted for a timing tower.' },
  { key: 'session', label: 'session', hint: 'Event and session title, flag, clock, laps to go, fastest lap.' },
  { key: 'driver', label: 'driver', hint: 'The car picked on the Live screen, for a driver card or lower third.' },
  { key: 'battle', label: 'battle', hint: 'Two cars, the gap between them and whether it is closing.' },
  { key: 'events', label: 'events', hint: 'New leader, fastest lap, flag, pit in/out.' },
  { key: 'championship', label: 'championship', hint: 'Live championship standings: the top five as if the race ended now. Needs a championship set in step 4.' },
  { key: 'health', label: 'health', hint: 'Diagnostics, for a debug overlay. Not for air.' },
];

const emptyStreams = () => Object.fromEntries(STREAMS.map(s => [s.key, '']));

/** A saved preset that holds data stream tokens: a race series to send to. */
function isSeries(preset) {
  return preset?.config?.kind === 'series' || Object.values(preset?.config?.streams || {}).some(Boolean);
}

/**
 * Screen 1 — Setup.
 *
 * Paste the timing page and check it, pick the race series, start. A race
 * series (BSM, HKRC…) carries the Singular data stream tokens and its class
 * names and colours, because those stay the same from race to race while the
 * timing URL changes every event — so an operator never needs to know a token.
 * A new series is added once, from the same dropdown.
 */
export default function SetupPanel({ show, running, onStarted }) {
  const [provider, setProvider] = useState('auto');
  const [input, setInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const [presets, setPresets] = useState([]);
  const [seriesId, setSeriesId] = useState('');
  const [editingKeys, setEditingKeys] = useState(false);
  const [newName, setNewName] = useState('');
  const [streams, setStreams] = useState(emptyStreams);
  const [moreStreams, setMoreStreams] = useState(false);
  const [savingSeries, setSavingSeries] = useState(false);

  const [tuning, setTuning] = useState({ poll_interval_ms: 1000, visible_rows: 30 });
  const [classFilter, setClassFilter] = useState('');
  const [classColours, setClassColours] = useState({});
  const [championships, setChampionships] = useState([]);
  const [savingChamps, setSavingChamps] = useState(false);
  const [photosFolder, setPhotosFolder] = useState('');
  const [photoCheck, setPhotoCheck] = useState(null);
  const [checkingPhotos, setCheckingPhotos] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => { loadPresets(); }, []);

  function loadPresets() {
    return toolsAPI.presets(TOOL_KEY).then(res => setPresets(res.data || [])).catch(() => setPresets([]));
  }

  const seriesList = presets.filter(isSeries);
  const series = seriesList.find(s => s.id === seriesId) || null;
  const adding = seriesId === ADD_SERIES;

  // Series are saved per workspace, and a workspace is usually one client —
  // so when there is only one, it is the answer.
  useEffect(() => {
    if (!seriesId && seriesList.length === 1) chooseSeries(seriesList[0].id);
  }, [presets]); // eslint-disable-line react-hooks/exhaustive-deps

  async function check(override) {
    const value = (typeof override === 'string' ? override : input).trim();
    if (!value) return;
    setChecking(true);
    setError('');
    try {
      const res = await timingBridgeAPI.resolve({ provider, input: value });
      setPreview(res.data);
      if (provider === 'auto') setProvider(res.data.provider);
    } catch (err) {
      setPreview(null);
      setError(err.response?.data?.error || err.message || 'Could not reach the timing site');
    } finally {
      setChecking(false);
    }
  }

  function chooseSeries(id) {
    setSeriesId(id);
    setEditingKeys(false);
    setStreams(emptyStreams());
    setMoreStreams(false);
    const chosen = seriesList.find(s => s.id === id);
    // A series brings its class names and colours with it.
    setClassColours(chosen?.config?.class_colours || {});
    // …and the championships it runs, set up once for the series.
    setChampionships(chosen?.config?.championships || []);
    // …and where its riders' pictures are.
    setPhotosFolder(chosen?.config?.photos_folder || '');
    setPhotoCheck(null);
    if (chosen?.config?.tuning) setTuning(t => ({ ...t, ...chosen.config.tuning }));
  }

  /** Tokens typed here; a blank field is not sent, so a stored token stands. */
  const typedStreams = () => Object.fromEntries(STREAMS.filter(s => streams[s.key].trim()).map(s => [s.key, streams[s.key].trim()]));

  async function saveSeries() {
    setSavingSeries(true);
    setError('');
    try {
      const config = { kind: 'series', streams: typedStreams(), class_colours: cleanColours(classColours) };
      const res = adding
        ? await toolsAPI.createPreset(TOOL_KEY, { name: newName.trim(), config })
        : await toolsAPI.updatePreset(TOOL_KEY, series.id, { config });
      await loadPresets();
      setSeriesId(res.data.id);
      setEditingKeys(false);
      setStreams(emptyStreams());
      setNewName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the race series');
    } finally {
      setSavingSeries(false);
    }
  }

  /** The championships belong to the series, so next meeting they are already there. */
  async function saveChampionships() {
    setSavingChamps(true);
    setError('');
    try {
      await toolsAPI.updatePreset(TOOL_KEY, series.id, { config: { championships: cleanChampionships(championships), photos_folder: photosFolder.trim() || null } });
      await loadPresets();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the championships');
    } finally {
      setSavingChamps(false);
    }
  }

  async function checkPhotos() {
    setCheckingPhotos(true);
    try {
      const res = await timingBridgeAPI.checkPhotos(photosFolder.trim());
      setPhotoCheck(res.data);
    } catch (err) {
      setPhotoCheck(err.response?.data || { ok: false, error: err.message });
    } finally {
      setCheckingPhotos(false);
    }
  }

  async function handleStart() {
    setStarting(true);
    setError('');
    try {
      const res = await timingBridgeAPI.start({
        show_id: show.id,
        provider,
        input: input.trim(),
        tuning,
        class_filter: classFilter || null,
        class_colours: cleanColours(classColours),
        streams_preset_id: series.id,
        championships: cleanChampionships(championships),
        photos_folder: photosFolder.trim() || null,
      });
      onStarted(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not start live timing');
    } finally {
      setStarting(false);
    }
  }

  const classes = preview?.session?.classes || [];
  const seriesStreams = STREAMS.filter(s => series?.config?.streams?.[s.key]);
  const canStart = !!show && !!preview && !!series && seriesStreams.length > 0 && !running && !starting;
  const showKeyFields = adding || editingKeys;

  return (
    <div className="space-y-6">
      {error && <div className="px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>}

      {/* 1. The timing page */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-white">1. The timing page</h2>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2 items-end">
          <div>
            <label className={LABEL}>Live timing URL</label>
            <input
              placeholder="Paste the live timing page (Speedhive, Alpha Timing or Apex Timing)"
              value={input}
              onChange={e => { setInput(e.target.value); setPreview(null); }}
              onKeyDown={e => e.key === 'Enter' && check()}
              className={FIELD}
            />
          </div>
          <div>
            <label className={LABEL}>Timing system</label>
            <select value={provider} onChange={e => setProvider(e.target.value)} className={FIELD}>
              {PROVIDERS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <button onClick={check} disabled={checking || !input.trim()} className={BUTTON}>
            {checking ? 'Checking…' : 'Check'}
          </button>
        </div>
        <p className="text-[10px] text-gray-600 leading-relaxed">
          A URL says which system it is. A bare name (<code className="text-gray-500">ukc</code>, <code className="text-gray-500">hkrc</code>)
          needs the timing system chosen. Speedhive also follows an organiser rather than one event: give their account code, the letters
          before the dash in any of their event links (<code className="text-gray-500">KVNSNDLQ</code> for BSSC), or their Speedhive login,
          and it finds whatever event they have live, listed on the Speedhive website or not. Their results organisation number or page
          (<code className="text-gray-500">19155</code>, speedhive.mylaps.com/organizations/19155) works too while one of their recent events is still on Speedhive's live service.
        </p>

        {preview && (
          <div className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wide text-gray-500">{PROVIDERS.find(p => p.key === preview.provider)?.label}</span>
              <span className="text-sm text-white font-medium">{preview.session?.event || preview.resolved?.label}</span>
              {preview.session && <span className="text-sm text-gray-400">{preview.session.title}</span>}
              {preview.session?.flag && <FlagPill flag={preview.session.flag} />}
            </div>
            <a href={preview.url} target="_blank" rel="noreferrer" className="block text-[11px] text-blue-500 hover:text-blue-400 truncate">{preview.url}</a>
            {preview.resolved?.org && (
              <div className="text-[11px] text-gray-400">
                Following the organiser <span className="text-gray-200">{preview.resolved.org}</span>: whichever event they have live, today or next weekend.
              </div>
            )}
            {!preview.resolved?.org && preview.resolved?.account && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400">
                <span>
                  Run by <span className="text-gray-200">{preview.resolved.organiser || preview.resolved.account}</span>, account <code className="text-gray-300">{preview.resolved.account}</code>.
                </span>
                <button
                  onClick={() => { setInput(preview.resolved.account); check(preview.resolved.account); }}
                  disabled={checking}
                  className="text-blue-500 hover:text-blue-400 disabled:opacity-40"
                >
                  Follow this organiser instead
                </button>
                <span className="text-gray-600">so it finds their live event by itself, with nothing to paste next time.</span>
              </div>
            )}
            {preview.live
              ? (
                <>
                  <div className="text-xs text-gray-500">
                    {preview.session.rowCount} cars
                    {preview.session.clockRemaining && ` · ${preview.session.clockRemaining} remaining`}
                    {preview.session.lapsToGo != null && ` · ${preview.session.lapsToGo} laps to go`}
                  </div>
                  <RunningOrder rows={preview.session.rows} compact />
                </>
              )
              : <div className="text-xs text-amber-400">{preview.note}</div>}
          </div>
        )}
      </section>

      {/* 2. The race series: where the graphics are */}
      {preview && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-white">2. Race series</h2>
          <select value={seriesId} onChange={e => chooseSeries(e.target.value)} className={FIELD}>
            <option value="">Choose the race series</option>
            {seriesList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            <option value={ADD_SERIES}>Add a race series…</option>
          </select>

          {series && !editingKeys && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-gray-500 mr-1">Publishes to</span>
              {seriesStreams.map(s => (
                <span key={s.key} className="px-2 py-0.5 rounded text-[11px] bg-[#111111] border border-[#2a2a2a] text-gray-300" title={s.hint}>{s.label}</span>
              ))}
              {seriesStreams.length === 0 && <span className="text-[11px] text-amber-400">no data streams yet</span>}
              <button onClick={() => setEditingKeys(true)} className="ml-auto text-[11px] text-blue-500 hover:text-blue-400">Change data streams</button>
            </div>
          )}

          {showKeyFields && (
            <div className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded space-y-3">
              {adding && (
                <div>
                  <label className={LABEL}>Series name</label>
                  <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. MXGB" className={FIELD} />
                </div>
              )}
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Each stream's <strong className="text-gray-400">private token</strong>, from the Data Stream's settings in Singular.
                Set once for the series; nobody picking it later sees them.
                {!adding && ' A field left blank keeps the token already saved.'}
              </p>
              {STREAMS.filter(s => moreStreams || s.key === 'legacy').map(stream => (
                <div key={stream.key}>
                  <label className={LABEL}>
                    {stream.label}
                    {series?.config?.streams?.[stream.key] && <span className="ml-2 normal-case text-gray-600">(saved)</span>}
                  </label>
                  <input
                    type="password"
                    placeholder={series?.config?.streams?.[stream.key] ? 'keep the saved token' : 'private token'}
                    value={streams[stream.key]}
                    onChange={e => setStreams(st => ({ ...st, [stream.key]: e.target.value }))}
                    className={FIELD}
                  />
                  <p className="text-[10px] text-gray-600 mt-1">{stream.hint}</p>
                </div>
              ))}
              {!moreStreams && (
                <button onClick={() => setMoreStreams(true)} className="text-[11px] text-blue-500 hover:text-blue-400">
                  Add the new per-graphic streams (tower, session, driver, battle…)
                </button>
              )}
              <div className="flex gap-2 items-center">
                <button
                  onClick={saveSeries}
                  disabled={savingSeries || (adding && !newName.trim())}
                  className={BUTTON}
                >
                  {savingSeries ? 'Saving…' : adding ? 'Save race series' : `Save ${series?.name}`}
                </button>
                {!adding && <button onClick={() => { setEditingKeys(false); setStreams(emptyStreams()); }} className="text-xs text-gray-500 hover:text-gray-300">Cancel</button>}
              </div>
            </div>
          )}
        </section>
      )}

      {/* 3. Tower and classes */}
      {preview && series && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-white">3. Tower and classes</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className={LABEL}>Tower class</label>
              <select value={classFilter} onChange={e => setClassFilter(e.target.value)} className={FIELD}>
                <option value="">All classes</option>
                {classes.map(c => <option key={c.code} value={c.code}>{classColours[c.code]?.name || c.name} ({c.count})</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Tower rows</label>
              <input type="number" min={1} max={80} value={tuning.visible_rows}
                onChange={e => setTuning(t => ({ ...t, visible_rows: Number(e.target.value) }))} className={FIELD} />
            </div>
            <div>
              <label className={LABEL}>Poll (ms)</label>
              <input type="number" min={500} max={30000} step={250} value={tuning.poll_interval_ms}
                onChange={e => setTuning(t => ({ ...t, poll_interval_ms: Number(e.target.value) }))} className={FIELD} />
            </div>
          </div>

          {classes.length > 0 && (
            <>
              <div className="border border-[#2a2a2a] rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#1a1a1a] text-gray-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Class as sent</th>
                      <th className="text-left px-3 py-2 font-medium">Display name</th>
                      <th className="text-left px-3 py-2 font-medium">Background</th>
                      <th className="text-left px-3 py-2 font-medium">Text</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map(c => {
                      const value = classColours[c.code] || {};
                      const set = (field, v) => setClassColours(cc => ({ ...cc, [c.code]: { ...(cc[c.code] || {}), [field]: v } }));
                      return (
                        <tr key={c.code} className="border-t border-[#2a2a2a]">
                          <td className="px-3 py-1.5 text-gray-300">
                            <span className="inline-block w-2.5 h-2.5 rounded-sm mr-2 align-middle border border-[#3a3a3a]" style={{ background: value.bg || c.bg || 'transparent' }} />
                            {c.code} <span className="text-gray-600">· {c.count}</span>
                          </td>
                          <td className="px-2 py-1"><input value={value.name || ''} placeholder={c.name} onChange={e => set('name', e.target.value)} className={FIELD} /></td>
                          <td className="px-2 py-1"><input value={value.bg || ''} placeholder={c.bg || '#1AA3EB'} onChange={e => set('bg', e.target.value)} className={FIELD} /></td>
                          <td className="px-2 py-1"><input value={value.fg || ''} placeholder={c.fg || '#FFFFFF'} onChange={e => set('fg', e.target.value)} className={FIELD} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={saveSeries} disabled={savingSeries} className="text-[11px] text-blue-500 hover:text-blue-400 disabled:opacity-40 flex-shrink-0">
                  {savingSeries ? 'Saving…' : `Save these classes to ${series.name}`}
                </button>
                <span className="text-[10px] text-gray-600">
                  Names and colours set here replace the timing site's (and, for Apex, the old scraper's blue/yellow rule).
                </span>
              </div>
            </>
          )}
        </section>
      )}

      {/* 4. Live championships (optional) */}
      {preview && series && (
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-white">4. Live championships <span className="text-gray-500 font-normal">(optional)</span></h2>
            <button onClick={saveChampionships} disabled={savingChamps} className="text-[11px] text-blue-500 hover:text-blue-400 disabled:opacity-40">
              {savingChamps ? 'Saving…' : `Save these to ${series.name}`}
            </button>
          </div>
          <p className="text-[10px] text-gray-600 leading-relaxed">
            Each championship is published as if the race on track ended now, to the <strong className="text-gray-400">championship</strong> stream:
            the class on track at the top level, every championship under <code className="text-gray-500">all</code>. Every rider is counted, so a
            leader running 15th shows where that leaves them. A finished race is banked when the next session starts, so moto 2 builds on moto 1.
          </p>
          <ChampionshipEditor value={championships} onChange={setChampionships} />
          {cleanChampionships(championships).length > 0 && !series?.config?.streams?.championship && (
            <p className="text-[10px] text-amber-500">{series.name} has no championship data stream yet. Add one with "Change data streams" or the standings will not be published.</p>
          )}
          <PhotosFolderField value={photosFolder} onChange={(v) => { setPhotosFolder(v); setPhotoCheck(null); }} onCheck={checkPhotos} checking={checkingPhotos} result={photoCheck} seriesName={series.name} />
        </section>
      )}

      {preview && (
        <section className="space-y-2 pt-2 border-t border-[#2a2a2a]">
          <button
            onClick={handleStart}
            disabled={!canStart}
            className="w-full px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm font-medium rounded transition-colors"
          >
            {starting ? 'Starting…' : running ? 'Already running, stop it first' : series ? `Start live timing for ${series.name}` : 'Start live timing'}
          </button>
          {!series && <p className="text-[10px] text-amber-500">Choose the race series. It decides which Singular graphics this goes to.</p>}
          {series && seriesStreams.length === 0 && <p className="text-[10px] text-amber-500">{series.name} has no data streams yet. Use "Change data streams".</p>}
          {!show && <p className="text-[10px] text-amber-500">Pick a show first. Live timing binds to one, and its session is recorded against it.</p>}
        </section>
      )}
    </div>
  );
}

/** Drop empty class rows, so a blank editor does not override the site's colours. */
function cleanColours(map) {
  const out = {};
  for (const [code, v] of Object.entries(map || {})) {
    const entry = Object.fromEntries(Object.entries(v || {}).filter(([, value]) => value && String(value).trim()));
    if (Object.keys(entry).length) out[code] = entry;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * The race series' folder of rider pictures. Checked here, before the show,
 * so a folder nobody shared turns up now rather than on air.
 */
function PhotosFolderField({ value, onChange, onCheck, checking, result, seriesName }) {
  return (
    <div className="pt-2 space-y-1.5">
      <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide">Rider photos (optional)</label>
      <div className="flex gap-2">
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Google Drive folder link"
          className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
        />
        <button
          onClick={onCheck}
          disabled={!value.trim() || checking}
          className="px-3 py-1.5 text-xs rounded border border-[#2a2a2a] bg-[#111111] text-gray-300 hover:border-[#3a3a3a] disabled:opacity-40"
        >
          {checking ? 'Checking…' : 'Check folder'}
        </button>
      </div>
      <p className="text-[10px] text-gray-600 leading-relaxed">
        Name each picture with the race number, the surname or both, like <code className="text-gray-500">426 Mewse.png</code>. The championship stream
        then carries each rider's photo, for the champion strap and podium. Saved to {seriesName} with the championships; re-read every 5 minutes while running.
      </p>
      {result?.ok && (
        <p className="text-[11px] text-green-400">
          Found {result.file_count} picture{result.file_count === 1 ? '' : 's'}.
          {result.file_count > 0 && <span className="text-gray-500"> {result.files.slice(0, 6).join(', ')}{result.file_count > 6 ? '…' : ''}</span>}
        </p>
      )}
      {result && !result.ok && (
        <p className="text-[11px] text-amber-500">
          {result.error}
          {result.share_with && !String(result.error || '').includes(result.share_with) && <> Share it with <code className="text-amber-300 select-all">{result.share_with}</code>.</>}
        </p>
      )}
    </div>
  );
}
